import { z } from "zod";

import { RELEASE_NOTE_TEXT_MAX } from "../releaseNotes/text";
import { VERSION_PATTERN } from "../releaseNotes/version";

import { USER_ROLES, userRoleSchema } from "./enums";

// ---------------------------------------------------------------------------
// «Was ist neu» (NEU-1) — die Antworten von /api/release-notes, drizzle-frei.
// ---------------------------------------------------------------------------
// Frontend und Endpunkt lesen dasselbe Schema: laeuft Frontend und API
// waehrend eines Rollouts kurz auseinander, ist das ein lauter Fehler statt
// einer stillen Null (wie beim Kommandostand).

/** Die volle Liste im Nutzer-Menue zeigt die Zeilen der letzten 60 Tage. */
export const RELEASE_NOTES_LIST_DAYS = 60;
/** Der Kommandostand zeigt die Zeilen der letzten 14 Tage (auch ausgeblendete). */
export const RELEASE_NOTES_KOMMANDOSTAND_DAYS = 14;

export const releaseNoteVersionSchema = z
  .string()
  .trim()
  .regex(VERSION_PATTERN, "Version wie 6.638.0");

/** «alle» = die vier Rollen; sonst eine Teilmenge davon. */
export const releaseNoteAudienceSchema = z.union([
  z.literal("alle"),
  z.array(userRoleSchema).min(1).max(USER_ROLES.length),
]);
export type ReleaseNoteAudience = z.infer<typeof releaseNoteAudienceSchema>;

export const releaseNoteZeileSchema = z.object({
  id: z.string().uuid(),
  version: z.string(),
  text: z.string(),
  createdAt: z.string(),
  meldung: z
    .object({ text: z.string(), gemeldetAm: z.string() })
    .nullable(),
});
export type ReleaseNoteZeile = z.infer<typeof releaseNoteZeileSchema>;

/**
 * Das Fenster. `vertretung` = jemand schaut als jemand anderes: kein Fenster,
 * keine Marke. `leer` = nichts Neues (oder neuer Mensch, Marke still gesetzt).
 */
export const whatsNewFensterSchema = z.object({
  mode: z.enum(["fenster", "leer", "vertretung"]),
  serverVersion: z.string(),
  seenVersion: z.string().nullable(),
  meldungen: z.array(releaseNoteZeileSchema),
  neu: z.array(releaseNoteZeileSchema),
  weitere: z.number().int().nonnegative(),
});
export type WhatsNewFenster = z.infer<typeof whatsNewFensterSchema>;

export const whatsNewListeSchema = z.object({
  tage: z.number().int().positive(),
  zeilen: z.array(releaseNoteZeileSchema),
});
export type WhatsNewListe = z.infer<typeof whatsNewListeSchema>;

export const releaseNoteKommandostandZeileSchema = z.object({
  id: z.string().uuid(),
  version: z.string(),
  text: z.string(),
  audience: z.array(userRoleSchema),
  hiddenAt: z.string().nullable(),
  createdAt: z.string(),
  feedbackItemId: z.string().uuid().nullable(),
  melderName: z.string().nullable(),
  /** Die laufende Server-Version hat diese Version erreicht — das Team sieht sie. */
  live: z.boolean(),
});
export type ReleaseNoteKommandostandZeile = z.infer<
  typeof releaseNoteKommandostandZeileSchema
>;

export const releaseNotesKommandostandSchema = z.object({
  serverVersion: z.string(),
  tage: z.number().int().positive(),
  zeilen: z.array(releaseNoteKommandostandZeileSchema),
});
export type ReleaseNotesKommandostand = z.infer<
  typeof releaseNotesKommandostandSchema
>;

/** POST /api/release-notes — der eine Schreibweg (Superadmin). */
export const createReleaseNoteBodySchema = z
  .object({
    version: releaseNoteVersionSchema,
    // Die Laenge prueft der Sprachwaechter mit Klartext; zod deckelt nur grob.
    text: z.string().max(RELEASE_NOTE_TEXT_MAX * 4),
    audience: releaseNoteAudienceSchema,
    feedbackItemId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateReleaseNoteBody = z.infer<typeof createReleaseNoteBodySchema>;

/** PATCH /api/release-notes — ausblenden / wieder einblenden (Superadmin). */
export const hideReleaseNoteBodySchema = z
  .object({
    id: z.string().uuid(),
    hidden: z.boolean(),
  })
  .strict();

/** POST /api/release-notes/seen — «Verstanden». */
export const markSeenBodySchema = z
  .object({
    version: releaseNoteVersionSchema,
  })
  .strict();
